import { Model } from '@vuex-orm/core';
import Image from '@/models/Image';
import Imageable from '@/models/Imageable';
import Level from '@/models/Level';
import Levelable from '@/models/Levelable';
import Date from '@/models/Date';
import OptionCategory from '@/models/OptionCategory';
import Project from '@/models/Project';
import Projectable from '@/models/Projectable';

export default class Option extends Model {
  // This is the name used as module name of the Vuex Store.
  static entity = 'options';

  static state() {
    return {
      hasOptions: false,
    }
  }

  // List of all fields (schema) of the residence model.
  static fields() {
    return {
      id: this.uid(),
      name: this.attr(''),

      description: this.attr(null),

      price: this.number(0),
      priceIsVariable: this.boolean(false),
      amount: this.number(1),
      discount: this.number(0),

      thumbnail: this.attr(null),

      isStandard: this.boolean(false),
      isSelected: this.boolean(false),
      isMultiple: this.boolean(false),

      option_category_id: this.attr(null),

      hasExtraInfo: this.boolean(false),
      extraTitle: this.attr(null),
      extraDescription: this.attr(null),
      extraImages: this.attr(null),

      // Relationship IDs
      option_category_id: this.attr(null),

      // Relationships
      date: this.morphOne(Date, 'dateable_id', 'dateable_type'),
      images: this.morphToMany(
        Image,
        Imageable,
        'image_id',
        'imageable_id',
        'imageable_type'
      ),
      levels: this.morphToMany(
        Level,
        Levelable,
        'level_id',
        'levelable_id',
        'levelable_type'
      ),
      category: this.belongsTo(OptionCategory, 'option_category_id'),
      projects: this.morphToMany(
        Project,
        Projectable,
        'project_id',
        'projectable_id',
        'projectable_type'
      ),
    };
  }

  get dynamicPrice() {
    if (this.priceIsVariable === true) {
      return this.price * this.amount;
    } else {
      return this.price;
    }
  }

  get currentPrice() {
    if (this.priceIsVariable === true && this.isStandard === false) {
      return this.dynamicPrice - this.discount;
    } else {
      return this.dynamicPrice;
    }
  }

  static transformer(data) {
    const options = [];
    data.data.forEach(item => {
      let option = {};
      option.id = item.id;
      option.name = item.title.rendered;
      option.description = item.acf.description;
      option.price = item.acf.price;
      
      if (item.acf.variable_price === 'true') {
        option.priceIsVariable = true;
      }

      if (item.acf.standard_level) {
        option.levels = [];
        item.acf.standard_level.forEach(level => {
          option.levels.push({id: level.term_id});
        })
      }

      if (item.acf.project) {
        option.projects = [];
        item.acf.project.forEach(project => {
          option.projects.push({id: project.term_id});
        })
      }

      if (item.acf.category) {
        option.category = {
          id: item.acf.category.term_id,
        }
      }

      // Add date object to Option object
      option.date = {
        date: item.date,
        date_gmt: item.date_gmt,
        modified: item.modified,
        modified_gmt: item.modified_gmt
      };

      option.images = [];

      if (item.acf.thumbnail) {
        option.thumbnail = item.acf.thumbnail.id;
        let image = item.acf.thumbnail;
        option.images.push({
          id: image.id,
          filename: image.filename,
          title: image.title,
          alt: image.alt,
          caption: image.caption,
          description: image.description,
          mime_type: image.mime_type,
          type: image.type,
          subtype: image.subtype,
          url: image.url,
          width: image.width,
          height: image.height,
          date: {
            date: image.date,
            modified: image.modified,
          },
          sizes: [
            {
              name: 'thumbnail',
              url: image.sizes.thumbnail,
              width: image.sizes['thumbnail-width'],
              height: image.sizes['thumbnail-height'],
            },
            {
              name: 'medium',
              url: image.sizes.medium,
              width: image.sizes['medium-width'],
              height: image.sizes['medium-height'],
            },
            {
              name: 'medium_large',
              url: image.sizes.medium_large,
              width: image.sizes['medium_large-width'],
              height: image.sizes['medium_large-height'],
            },
            {
              name: 'large',
              url: image.sizes.large,
              width: image.sizes['large-width'],
              height: image.sizes['large-height'],
            },
            {
              name: '1536x1536',
              url: image.sizes['1536x1536'],
              width: image.sizes['1536x1536-width'],
              height: image.sizes['1536x1536-height'],
            },
            {
              name: '2048x2048',
              url: image.sizes['2048x2048'],
              width: image.sizes['2048x2048-width'],
              height: image.sizes['2048x2048-height'],
            },
          ],
        });
      }

      if (item.acf.multiple_choice === 'true') {
        option.isMultiple = true;
      }

      if (item.acf.extra_info === 'true') {
        option.hasExtraInfo = true;
        if (item.acf.extra_title) {
          option.extraTitle = item.acf.extra_title;
        }
        if (item.acf.extra_description) {
          option.extraDescription = item.acf.extra_description;
        }
        if (item.acf.extra_images) {
          option.extraImages = [];
          item.acf.extra_images.forEach(image => {
            option.images.push({
              id: image.id,
              filename: image.filename,
              title: image.title,
              alt: image.alt,
              caption: image.caption,
              description: image.description,
              mime_type: image.mime_type,
              type: image.type,
              subtype: image.subtype,
              url: image.url,
              width: image.width,
              height: image.height,
              date: {
                date: image.date,
                modified: image.modified,
              },
              sizes: [
                {
                  name: 'thumbnail',
                  url: image.sizes.thumbnail,
                  width: image.sizes['thumbnail-width'],
                  height: image.sizes['thumbnail-height'],
                },
                {
                  name: 'medium',
                  url: image.sizes.medium,
                  width: image.sizes['medium-width'],
                  height: image.sizes['medium-height'],
                },
                {
                  name: 'medium_large',
                  url: image.sizes.medium_large,
                  width: image.sizes['medium_large-width'],
                  height: image.sizes['medium_large-height'],
                },
                {
                  name: 'large',
                  url: image.sizes.large,
                  width: image.sizes['large-width'],
                  height: image.sizes['large-height'],
                },
                {
                  name: '1536x1536',
                  url: image.sizes['1536x1536'],
                  width: image.sizes['1536x1536-width'],
                  height: image.sizes['1536x1536-height'],
                },
                {
                  name: '2048x2048',
                  url: image.sizes['2048x2048'],
                  width: image.sizes['2048x2048-width'],
                  height: image.sizes['2048x2048-height'],
                },
              ],
            })
          })
        }
      }

      options.push(option);
    });
    return options;
  }

  static apiConfig = {
    dataTransformer: (data) => this.transformer(data),
  };

  static fetch(project, baseURL) {
    // TODO: Implementere paginert lookup.
    let url = '/wp-json/wp/v2/option?order=asc&per_page=100';
    if (project) {
      url = `${url}&project=${project}`;
    }
    return this.api().get(url, {
      baseURL: baseURL,
    });
  }
}
